"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.planPageBlockPart = planPageBlockPart;
exports.planPageElementPart = planPageElementPart;
const page_document_maps_1 = require("./page-document-maps");
const page_large_data_policy_1 = require("./page-large-data-policy");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_tree_1 = require("./page-structure-tree");
const page_structure_writer_1 = require("./page-structure-writer");
const writer_shard_id_1 = require("./writer-shard-id");
/** Selects storage for a new block without moving any existing entry. */
function planPageBlockPart(input) {
    if (input.formatVersion !== page_structure_writer_1.PAGE_FORMAT_VERSION)
        throw new page_structure_writer_1.PageStructureWriteError('Unsupported page format for part planning');
    const { entries, directory, parentId, writerPrefix, siblings } = input;
    const policy = input.policy ?? page_large_data_policy_1.PAGE_LARGE_DATA_POLICY.part;
    const at = Math.max(0, Math.min(input.index ?? siblings.length, siblings.length));
    const candidate = entries.get(siblings[at - 1] ?? '')?.s ??
        entries.get(siblings[at] ?? '')?.s ??
        entries.get(parentId)?.s ??
        page_document_maps_1.PAGE_DOCUMENT_PART_ID;
    const open = (partId) => partOpen(directory, partId, policy);
    if (open(candidate))
        return { partId: candidate, created: false };
    const own = [...directory.keys()]
        .filter(id => id.startsWith(writerPrefix) && open(id))
        .sort(compare)[0];
    return own === undefined
        ? {
            partId: (input.newPartId ?? (() => (0, writer_shard_id_1.createWriterShardId)(writerPrefix)))(),
            created: true,
        }
        : { partId: own, created: false };
}
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
// A part takes new entries while its measured bytes stay below the target.
function partOpen(directory, partId, policy) {
    const bytes = directory.get(partId)?.b ?? 0;
    return bytes < policy.targetBytes && bytes < policy.hardBytes;
}
/** Plans a new canvas element; existing entries are never changed. */
function planPageElementPart(input) {
    if (input.formatVersion !== page_structure_writer_1.PAGE_FORMAT_VERSION)
        throw new page_structure_writer_1.PageStructureWriteError('Unsupported page format for part planning');
    const { rect, unit, entries, directory, writerPrefix } = input;
    const policy = input.policy ?? page_large_data_policy_1.PAGE_LARGE_DATA_POLICY.part;
    if (!rect.every(Number.isFinite) || rect[2] < 0 || rect[3] < 0)
        throw new page_structure_writer_1.PageStructureWriteError('Invalid canvas rectangle');
    const result = (partId, created) => ({
        partId,
        created,
        rect: unionRect(directory.get(partId)?.r, rect),
    });
    if (unit !== undefined) {
        const id = 'mindmap' in unit ? unit.mindmap : unit.partOf;
        const entry = entries.get(id);
        if (!entry ||
            ('mindmap' in unit && entry.k !== page_structure_entry_1.PAGE_MINDMAP_KIND) ||
            !(0, page_structure_tree_1.isPageTreeEntry)(entry))
            throw new page_structure_writer_1.PageStructureWriteError(`Invalid page part unit ${id}`);
        if ((directory.get(entry.s)?.b ?? 0) >= policy.hardBytes)
            throw new page_structure_writer_1.PageStructureWriteError(`Page part unit ${id} is full`, 'page-part-unit-full');
        return result(entry.s, false);
    }
    // A small page stays one document: elements fill part zero up to the
    // target before the canvas splits into spatial parts.
    if (partOpen(directory, page_document_maps_1.PAGE_DOCUMENT_PART_ID, policy))
        return result(page_document_maps_1.PAGE_DOCUMENT_PART_ID, false);
    const x = rect[0] + rect[2] / 2, y = rect[1] + rect[3] / 2;
    const candidates = [...directory]
        .flatMap(([id, value]) => {
        if (!value.r ||
            value.b >= policy.targetBytes ||
            value.b >= policy.hardBytes)
            return [];
        const r = value.r;
        const distance = Math.hypot(Math.max(r[0] - x, 0, x - r[0] - r[2]), Math.max(r[1] - y, 0, y - r[1] - r[3]));
        return distance <= policy.canvasJoinDistance ? [{ id, distance }] : [];
    })
        .sort((a, b) => a.distance - b.distance || compare(a.id, b.id));
    // All eligible spatial parts include the writer's own parts as well.
    const candidate = candidates[0];
    return candidate
        ? result(candidate.id, false)
        : result((input.newPartId ?? (() => (0, writer_shard_id_1.createWriterShardId)(writerPrefix)))(), true);
}
function unionRect(previous, next) {
    if (!previous)
        return [...next];
    const x = Math.min(previous[0], next[0]), y = Math.min(previous[1], next[1]);
    return [
        x,
        y,
        Math.max(previous[0] + previous[2], next[0] + next[2]) - x,
        Math.max(previous[1] + previous[3], next[1] + next[3]) - y,
    ];
}
